import { Sparkle, TrendUp, TrendDown, Warning, Lightning } from "@phosphor-icons/react";

const PRIORITY_TONE = {
  high:   "bg-rose-50 border-rose-200 text-rose-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low:    "bg-slate-50 border-slate-200 text-slate-700",
};

export default function AIAnalysisTab({ doc, config, onAnalyse, busy }) {
  const pack = doc.pack;
  if (!pack) {
    return (
      <div className="bg-card border border-border rounded-2xl p-12 text-center card-elev" data-testid="idoc-analysis-empty">
        <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-4">
          <Sparkle size={28} weight="duotone" />
        </div>
        <div className="font-display text-2xl tracking-tight mb-2">Ready to analyse</div>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Complete your Investment Profile then run AI analysis to unlock scores, recommendations and every institutional document.</p>
        <button
          onClick={onAnalyse}
          disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 card-elev"
          data-testid="idoc-analysis-run"
        >
          <Sparkle size={14} weight="bold" /> Run AI analysis
        </button>
      </div>
    );
  }

  const dims = config?.score_dimensions || [];
  const scores = pack.scores || {};
  const recs = pack.recommendations || [];

  const avg = dims.length
    ? Math.round(dims.reduce((s, d) => s + (scores[d.key]?.score || 0), 0) / dims.length)
    : 0;

  return (
    <div className="space-y-8" data-testid="idoc-analysis-body">
      {/* Thesis */}
      {pack.investment_thesis_headline && (
        <div className="bg-gradient-to-br from-primary to-[hsl(var(--accent))] text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev-lg">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-2">Investment thesis</div>
          <div className="font-display text-2xl sm:text-3xl tracking-tight leading-tight max-w-3xl">{pack.investment_thesis_headline}</div>
          <div className="mt-6 flex items-baseline gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-75">Composite score</div>
              <div className="text-5xl font-display tracking-tight mt-1">{avg}<span className="text-lg opacity-70">/100</span></div>
            </div>
            <div className="text-xs opacity-80 max-w-md">Aggregate of the 10 dimensions below. Not investment advice.</div>
          </div>
        </div>
      )}

      {/* Scores */}
      <div>
        <div className="text-overline text-muted-foreground mb-4">Ten-dimension scorecard</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {dims.map((d) => {
            const s = scores[d.key] || {};
            const val = Number(s.score || 0);
            const tone = val >= 75 ? "teal" : val >= 50 ? "amber" : "rose";
            return (
              <div key={d.key} className="bg-card border border-border rounded-xl p-4 card-elev" data-testid={`score-${d.key}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-tight">{d.label}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`text-3xl font-display tracking-tight ${tone === "teal" ? "text-teal" : tone === "amber" ? "text-amber-600" : "text-rose-600"}`}>{val}</span>
                  <span className="text-xs text-muted-foreground">/100</span>
                </div>
                <div className="mt-3 h-1 rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full ${tone === "teal" ? "bg-teal" : tone === "amber" ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${val}%` }} />
                </div>
                <div className="mt-3 text-[11px] text-muted-foreground leading-snug line-clamp-3">{s.explanation}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      <div>
        <div className="text-overline text-muted-foreground mb-4">AI recommendations</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recs.map((r, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5 card-elev flex gap-4" data-testid={`reco-${i}`}>
              <div className="h-10 w-10 rounded-lg bg-teal-soft text-teal grid place-items-center flex-shrink-0">
                <Lightning size={18} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-display text-base tracking-tight">{r.title}</div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PRIORITY_TONE[r.priority] || PRIORITY_TONE.medium}`}>{r.priority}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{r.category}</div>
                <p className="text-sm text-foreground leading-relaxed">{r.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
